import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Broom, Warning, CheckCircle, Spinner } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function EmailAuditModal({ onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [scrubReport, setScrubReport] = useState(null);
  const [extraStr, setExtraStr] = useState("");

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-audit");
      setReport(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Audit failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, []);

  const scrub = async () => {
    if (!window.confirm(`Null out ${report?.total_matches || 0} email fields across landlords, addendums, PMR, subs, and notification events? This can't be undone.`)) return;
    setScrubbing(true);
    try {
      const extras = extraStr.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/email-audit/scrub",
                                      { extra_patterns: extras });
      setScrubReport(data);
      toast.success(`Scrubbed ${data.total_matches} email${data.total_matches === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scrub failed");
    } finally { setScrubbing(false); }
  };

  const active = scrubReport || report;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="email-audit-modal">
      <div className="max-w-2xl w-full bg-white border-2 border-[#0f172a] shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-red-50">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-red-800 flex items-center gap-1">
              <Broom size={12} weight="fill"/> Global Email Audit
            </div>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">
              {scrubReport ? "Scrub Report" : "Preview · Dry-Run"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="audit-close" className="h-10 w-10 flex items-center justify-center hover:bg-red-100">
            <X size={20}/>
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center gap-3 text-slate-700">
              <Spinner weight="bold" size={22} className="animate-spin"/>
              <span className="font-bold">Scanning every collection for test emails…</span>
            </div>
          )}

          {active && (
            <>
              {/* Headline */}
              <div className={`p-3 border-2 ${active.total_matches === 0 ? "border-emerald-500 bg-emerald-50" : (scrubReport ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50")}`} data-testid="audit-headline">
                <div className="flex items-center gap-2">
                  {active.total_matches === 0 || scrubReport ? (
                    <CheckCircle weight="fill" size={20} className="text-emerald-700"/>
                  ) : (
                    <Warning weight="fill" size={20} className="text-amber-700"/>
                  )}
                  <div className="font-display font-black text-2xl">
                    {active.total_matches}
                    <span className="text-sm font-bold text-slate-600 ml-1 tracking-normal">
                      {scrubReport ? "email fields cleared" : (active.total_matches === 0 ? "test emails found — clean!" : "test/dummy emails found")}
                    </span>
                  </div>
                </div>
                <div className="text-[10px] font-mono text-slate-500 mt-1">
                  {scrubReport ? "Scrubbed at" : "Scanned at"} {new Date(active.at).toLocaleString()} by {active.by}
                </div>
              </div>

              {/* Break-down */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <BreakDown label="Landlords · email" value={active.landlords.email} samples={active.landlords.samples.filter(s => s.field === "email")}/>
                <BreakDown label="Landlord notification chains" value={active.landlords.chain_emails} samples={active.landlords.samples.filter(s => s.field !== "email")}/>
                <BreakDown label="Alarm addendums · owner_email" value={active.addendums.owner_emails} samples={active.addendums.samples}/>
                <BreakDown label="PMR company · email" value={active.pmr.top_emails} samples={active.pmr.samples.filter(s => s.field === "email")}/>
                <BreakDown label="PMR company · contacts" value={active.pmr.contact_emails} samples={active.pmr.samples.filter(s => s.field !== "email")}/>
                <BreakDown label="Subcontractors · email" value={active.subs.emails} samples={active.subs.samples}/>
                <BreakDown label="Escalation events · step emails" value={active.notif_events.step_emails} samples={active.notif_events.samples}/>
              </div>

              {/* Patterns used */}
              <details className="p-2 border border-slate-300 text-xs">
                <summary className="cursor-pointer font-black uppercase tracking-widest text-[10px] text-slate-700">
                  Patterns used ({(active.patterns_used || []).length})
                </summary>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(active.patterns_used || []).map(p => (
                    <span key={p} className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 font-mono text-[10px]">{p}</span>
                  ))}
                </div>
              </details>

              {!scrubReport && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
                    Extra patterns (optional, comma-separated)
                  </div>
                  <input value={extraStr} onChange={e => setExtraStr(e.target.value)}
                    placeholder="e.g. @gopmr-old.com, temp@, staging-"
                    data-testid="audit-extra"
                    className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-mono"/>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Substring match, case-insensitive. Adds to the defaults for this scrub.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-3 border-t-2 border-[#0f172a] flex flex-wrap gap-2 justify-end bg-slate-50">
          <button onClick={onClose} className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
            {scrubReport ? "Done" : "Cancel"}
          </button>
          {!scrubReport && (
            <>
              <button onClick={loadPreview} disabled={loading} data-testid="audit-refresh"
                className="h-10 px-3 border-2 border-slate-800 bg-white font-black uppercase text-[11px] tracking-widest disabled:opacity-50">
                Refresh Preview
              </button>
              <button onClick={scrub} disabled={scrubbing || loading || (report?.total_matches || 0) === 0}
                data-testid="audit-scrub-btn"
                className="h-10 px-3 bg-red-600 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                <Broom size={14} weight="fill"/> {scrubbing ? "Scrubbing…" : `Scrub ${report?.total_matches || 0}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BreakDown({ label, value, samples }) {
  return (
    <div className={`p-2 bg-white border-2 ${value > 0 ? "border-amber-400" : "border-slate-200"}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-black text-lg">{value}</div>
      {samples && samples.length > 0 && (
        <div className="mt-1 space-y-0.5 text-[10px] text-slate-600 max-h-16 overflow-y-auto">
          {samples.slice(0, 3).map((s, i) => (
            <div key={i} className="truncate font-mono">
              {(s.name || s.address || s.id || "").slice(0, 30)} → <span className="text-red-700">{s.value}</span>
            </div>
          ))}
          {samples.length > 3 && <div className="italic">+{samples.length - 3} more…</div>}
        </div>
      )}
    </div>
  );
}
